import React, { useEffect, useState } from 'react';
import { rateCardAPI } from '../services/api';

const emptyCard = {
  ytDedicatedVideo: '', ytIntegration: '', ytShort: '', ytCommunityPost: '',
  igReelDedicated: '', igReelIntegration: '', igStaticPost: '', igStory: '', igStorySwipeup: '',
  twTweet: '', twThread: '',
  liPost: '', liArticle: '',
  bundleBasic: '', bundleBasicDescription: '',
  bundlePremium: '', bundlePremiumDescription: '',
  affiliateCommission: '', usageRightsPer30Days: '',
  notes: '', currencySymbol: '₹',
};

const TABS = ['YouTube', 'Instagram', 'Other Platforms', 'Packages & Extras'];

// Suggested ₹/view by niche — same 2025 India benchmark calibration as the
// public /tools/rate-card-generator (branded CPM ₹1,265–₹2,109 per 1,000 views,
// finance/tech premium). Used by the "Suggest rates" helper to pre-fill fields.
const NICHE_RATE = {
  'Finance / Business': 2.1, 'Tech / Gadgets': 1.9, 'Education': 1.6,
  'Beauty / Fashion': 1.5, 'Fitness / Health': 1.4, 'Food / Cooking': 1.3,
  'Travel': 1.4, 'Lifestyle / Vlog': 1.3, 'Gaming': 1.1,
  'Comedy / Entertainment': 1.1, 'Other': 1.3,
};
const NICHES = Object.keys(NICHE_RATE);
const roundTo = (x, step) => Math.max(step, Math.round(x / step) * step);

export default function RateCardPage() {
  const [card, setCard]         = useState(emptyCard);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tab, setTab]           = useState(0);
  const [showSuggest, setShowSuggest] = useState(false);
  const [sgNiche, setSgNiche]   = useState(NICHES[0]);
  const [sgViews, setSgViews]   = useState('');

  useEffect(() => {
    rateCardAPI.get().then(res => {
      if (!res.data.empty) {
        const d = res.data;
        setCard({
          ytDedicatedVideo:       d.ytDedicatedVideo       || '',
          ytIntegration:          d.ytIntegration          || '',
          ytShort:                d.ytShort                || '',
          ytCommunityPost:        d.ytCommunityPost        || '',
          igReelDedicated:        d.igReelDedicated        || '',
          igReelIntegration:      d.igReelIntegration      || '',
          igStaticPost:           d.igStaticPost           || '',
          igStory:                d.igStory                || '',
          igStorySwipeup:         d.igStorySwipeup         || '',
          twTweet:                d.twTweet                || '',
          twThread:               d.twThread               || '',
          liPost:                 d.liPost                 || '',
          liArticle:              d.liArticle              || '',
          bundleBasic:            d.bundleBasic            || '',
          bundleBasicDescription: d.bundleBasicDescription || '',
          bundlePremium:          d.bundlePremium          || '',
          bundlePremiumDescription: d.bundlePremiumDescription || '',
          affiliateCommission:    d.affiliateCommission    || '',
          usageRightsPer30Days:   d.usageRightsPer30Days   || '',
          notes:                  d.notes                  || '',
          currencySymbol:         d.currencySymbol         || '₹',
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const f = (updates) => setCard(c => ({ ...c, ...updates }));

  // Pre-fill all rate fields from niche + avg views (user edits afterwards).
  const applySuggestions = () => {
    const views = parseInt(sgViews, 10) || 0;
    if (!views) { alert('Enter your average views to get suggested rates.'); return; }
    const base = views * (NICHE_RATE[sgNiche] || 1.3);
    const v = (mult) => { const x = base * mult; return x > 0 ? String(roundTo(x, x >= 20000 ? 1000 : 500)) : ''; };
    f({
      ytDedicatedVideo: v(2.5), ytIntegration: v(1.0), ytShort: v(0.4), ytCommunityPost: v(0.15),
      igReelDedicated: v(1.0), igReelIntegration: v(0.6), igStaticPost: v(0.5), igStory: v(0.12), igStorySwipeup: v(0.18),
      bundleBasic: v(1.26), bundleBasicDescription: card.bundleBasicDescription || '1 Integration + 1 Short / Reel',
      bundlePremium: v(2.68), bundlePremiumDescription: card.bundlePremiumDescription || '1 Dedicated video + 1 Short + 2 Stories',
      usageRightsPer30Days: v(0.25),
    });
    setShowSuggest(false);
    setTab(0);
  };

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      await rateCardAPI.save(card);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await rateCardAPI.downloadPdf();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'rate-card.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Save your rate card first before downloading PDF'); }
    setDownloading(false);
  };

  const sym = card.currencySymbol || '₹';

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading…</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: 860, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e' }}>Rate Card 💰</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.875rem' }}>Set your rates per platform & format — download as PDF to share with brands</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button onClick={() => setShowSuggest(s => !s)} style={outlineBtn}>✨ Suggest rates</button>
          <button onClick={handleSave} disabled={saving} style={primaryBtn}>
            {saving ? 'Saving…' : saved ? '✅ Saved!' : '💾 Save Rates'}
          </button>
          <button onClick={handleDownload} disabled={downloading} style={outlineBtn}>
            {downloading ? '⏳' : '⬇️ Download PDF'}
          </button>
        </div>
      </div>

      {/* Suggest rates helper */}
      {showSuggest && (
        <div style={{ background: '#f0f9f4', border: '1px solid #c8ebd8', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
          <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1a7a4a', marginBottom: '0.4rem' }}>✨ Suggest my rates</div>
          <p style={{ fontSize: '0.78rem', color: '#666', margin: '0 0 0.8rem' }}>
            Based on 2025 India benchmarks (~₹1,265–₹2,109 per 1,000 views), adjusted for niche. Fills the rates below — <b>edit any field</b> after, then Save. A starting point, not a fixed price.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ ...labelStyle, flex: 1, minWidth: 160 }}>Niche
              <select value={sgNiche} onChange={e => setSgNiche(e.target.value)} style={input}>
                {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label style={{ ...labelStyle, flex: 1, minWidth: 140 }}>Avg views per video
              <input value={sgViews} onChange={e => setSgViews(e.target.value)} style={input} type="number" placeholder="e.g. 20000" />
            </label>
            <button onClick={applySuggestions} style={primaryBtn}>Fill suggested rates</button>
          </div>
        </div>
      )}

      {/* Tip */}
      <div style={{ background: '#e8f5e9', border: '1px solid #c8ebd8', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.82rem', color: '#2e7d32' }}>
        💡 <b>Leave blank</b> any format you don't offer — it won't appear on the PDF. All rates are exclusive of GST.
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', background: '#f5f5f5', padding: '0.3rem', borderRadius: 12 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            flex: 1, padding: '0.55rem', border: 'none', borderRadius: 9, cursor: 'pointer',
            fontWeight: 700, fontSize: '0.78rem',
            background: tab === i ? 'white' : 'transparent',
            color: tab === i ? '#1a7a4a' : '#888',
            boxShadow: tab === i ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}>{t}</button>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: 16, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

        {/* YouTube */}
        {tab === 0 && (
          <div>
            <PlatformHeader color="#ff0000" label="▶ YouTube" />
            <div style={grid2}>
              <RateField label="Dedicated Video" hint="Full video about brand only" sym={sym}
                value={card.ytDedicatedVideo} onChange={v => f({ ytDedicatedVideo: v })} />
              <RateField label="Integration (60–90 sec)" hint="Sponsored segment inside a regular video" sym={sym}
                value={card.ytIntegration} onChange={v => f({ ytIntegration: v })} />
              <RateField label="YouTube Short" hint="Dedicated short-form video" sym={sym}
                value={card.ytShort} onChange={v => f({ ytShort: v })} />
              <RateField label="Community Post" hint="Text/image post on community tab" sym={sym}
                value={card.ytCommunityPost} onChange={v => f({ ytCommunityPost: v })} />
            </div>
          </div>
        )}

        {/* Instagram */}
        {tab === 1 && (
          <div>
            <PlatformHeader color="#c13584" label="📸 Instagram" />
            <div style={grid2}>
              <RateField label="Dedicated Reel" hint="Full reel about brand" sym={sym}
                value={card.igReelDedicated} onChange={v => f({ igReelDedicated: v })} />
              <RateField label="Reel Integration" hint="Brand mention in a regular reel" sym={sym}
                value={card.igReelIntegration} onChange={v => f({ igReelIntegration: v })} />
              <RateField label="Static Post" hint="Feed photo/carousel post" sym={sym}
                value={card.igStaticPost} onChange={v => f({ igStaticPost: v })} />
              <RateField label="Story (per frame)" hint="Single story frame" sym={sym}
                value={card.igStory} onChange={v => f({ igStory: v })} />
              <RateField label="Story with Swipe-Up" hint="Story with link sticker" sym={sym}
                value={card.igStorySwipeup} onChange={v => f({ igStorySwipeup: v })} />
            </div>
          </div>
        )}

        {/* Other Platforms */}
        {tab === 2 && (
          <div>
            <PlatformHeader color="#141414" label="𝕏 Twitter / X" />
            <div style={{ ...grid2, marginBottom: '1.5rem' }}>
              <RateField label="Tweet" hint="Single sponsored tweet" sym={sym}
                value={card.twTweet} onChange={v => f({ twTweet: v })} />
              <RateField label="Thread" hint="Multi-tweet sponsored thread" sym={sym}
                value={card.twThread} onChange={v => f({ twThread: v })} />
            </div>
            <PlatformHeader color="#0077b5" label="in LinkedIn" />
            <div style={grid2}>
              <RateField label="Post" hint="Sponsored LinkedIn post" sym={sym}
                value={card.liPost} onChange={v => f({ liPost: v })} />
              <RateField label="Article / Newsletter" hint="Long-form LinkedIn article" sym={sym}
                value={card.liArticle} onChange={v => f({ liArticle: v })} />
            </div>
          </div>
        )}

        {/* Packages & Extras */}
        {tab === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={sectionLabel}>🎁 Packages</div>

            <div style={grid2}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={labelStyle}>Basic Package Price ({sym})
                  <input value={card.bundleBasic} onChange={e => f({ bundleBasic: e.target.value })}
                    style={input} type="number" placeholder="25000" />
                </label>
                <label style={labelStyle}>What's included?
                  <textarea value={card.bundleBasicDescription} onChange={e => f({ bundleBasicDescription: e.target.value })}
                    style={{ ...input, height: 70, resize: 'vertical' }}
                    placeholder="e.g. 1 YouTube integration + 1 Instagram Reel + 2 Stories" />
                </label>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={labelStyle}>Premium Package Price ({sym})
                  <input value={card.bundlePremium} onChange={e => f({ bundlePremium: e.target.value })}
                    style={input} type="number" placeholder="60000" />
                </label>
                <label style={labelStyle}>What's included?
                  <textarea value={card.bundlePremiumDescription} onChange={e => f({ bundlePremiumDescription: e.target.value })}
                    style={{ ...input, height: 70, resize: 'vertical' }}
                    placeholder="e.g. 1 YouTube dedicated video + 2 Reels + 5 Stories + 1 tweet" />
                </label>
              </div>
            </div>

            <div style={sectionLabel}>➕ Extras</div>

            <div style={grid2}>
              <label style={labelStyle}>Affiliate Commission
                <input value={card.affiliateCommission} onChange={e => f({ affiliateCommission: e.target.value })}
                  style={input} placeholder="e.g. 10% per sale via my link" />
              </label>
              <label style={labelStyle}>Usage Rights / Whitelisting (per 30 days, {sym})
                <input value={card.usageRightsPer30Days} onChange={e => f({ usageRightsPer30Days: e.target.value })}
                  style={input} type="number" placeholder="5000" />
              </label>
            </div>

            <div style={sectionLabel}>📌 Terms & Notes</div>
            <label style={labelStyle}>
              Notes (shown on PDF)
              <textarea value={card.notes} onChange={e => f({ notes: e.target.value })}
                style={{ ...input, height: 80, resize: 'vertical' }}
                placeholder="e.g. All rates exclusive of 18% GST. 50% advance required. Content delivered within 7 working days. Revision: 1 round included." />
            </label>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
        {tab > 0 && <button onClick={() => setTab(t => t - 1)} style={outlineBtn}>← Previous</button>}
        {tab < TABS.length - 1
          ? <button onClick={() => setTab(t => t + 1)} style={primaryBtn}>Next →</button>
          : <button onClick={handleSave} disabled={saving} style={primaryBtn}>
              {saving ? 'Saving…' : saved ? '✅ Saved!' : '💾 Save Rate Card'}
            </button>
        }
      </div>
    </div>
  );
}

// Sub-components
function PlatformHeader({ color, label }) {
  return (
    <div style={{ fontWeight: 800, fontSize: '0.9rem', color, marginBottom: '0.85rem',
      paddingBottom: '0.4rem', borderBottom: `2px solid ${color}33` }}>
      {label}
    </div>
  );
}

function RateField({ label, hint, sym, value, onChange }) {
  return (
    <label style={labelStyle}>
      {label}
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: '0.9rem' }}>{sym}</span>
        <input value={value} onChange={e => onChange(e.target.value)}
          style={{ ...input, paddingLeft: '1.6rem', width: '100%', boxSizing: 'border-box' }}
          type="number" placeholder="0" />
      </div>
      {hint && <span style={{ fontSize: '0.72rem', color: '#aaa' }}>{hint}</span>}
    </label>
  );
}

// Styles
const primaryBtn = { background: '#1a7a4a', color: 'white', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const outlineBtn = { background: 'white', color: '#1a7a4a', border: '1.5px solid #1a7a4a', borderRadius: 10, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const input = { padding: '0.55rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'white' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: '#444' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem' };
const sectionLabel = { fontWeight: 800, fontSize: '0.8rem', color: '#1a7a4a', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.3rem', borderBottom: '2px solid #e8f5e9' };
